import React, {
    useState, useRef, useCallback, useEffect, useMemo,
} from 'react';
import {
    Box, Button, Chip, Typography, useTheme, useMediaQuery,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import GuidedEntryRunChart from './GuidedEntryRunChart';

// ---------------------------------------------------------------------------
// GuidedEntry — reusable fullscreen sequential measurement input
//
// Props
// -----
// contextLabel  string            Title shown in the sticky header (e.g. study
//                                 name, or "Capability — Guided Entry").
// slots         Array<{label}>    Ordered list of cells to fill.  Each element
//                                 needs at least a `label` string that is shown
//                                 as the "what are you measuring" prompt.  Extra
//                                 fields are passed through opaquely and returned
//                                 in onSubmitValue so the caller can map back to
//                                 its data structure.
//                                 Pass an empty array to use appendMode.
// values        Array<number|null> Parallel array of current values for each
//                                 slot.  Used for display and for tracking which
//                                 slots remain unfilled.
//                                 For appendMode, pass the current list of values.
// onSubmitValue (value, slotIndex) => void
//                                 Called when the user confirms a value.
//                                 slotIndex is the index into `slots` that was
//                                 just filled (or values.length for appendMode).
// onClose       () => void        Called when the user presses "Back".
// backLabel     string            Text for the back button.  Default "Back to Table".
// submitLabel   string            Text for the submit button.  Default "Record (Enter)".
// appendMode    boolean           When true (Type 1 behaviour) the component
//                                 appends each value without a fixed slot list.
//                                 slotIndex passed to onSubmitValue equals
//                                 values.length at submit time.  Default false.
// specLimits    {usl,lsl,target}  Optional. When provided, USL/LSL/Target are
//                                 shown as chips in the pinned context strip
//                                 (above the input) and drawn as reference
//                                 lines on the run chart. Any field may be
//                                 omitted/null; number|string accepted.
//                                 Default null (no spec chips/lines).
// contextChips  Array<{label,value}>  Optional. Extra caller-defined chips
//                                 shown in the context strip (e.g. a single
//                                 tolerance value for GRR studies that don't
//                                 hold separate USL/LSL). Default [].
// ---------------------------------------------------------------------------

const GUIDED_BG = '#0d1117';

// Reads window.visualViewport (resize + scroll) so the fullscreen overlay can
// be sized/positioned to the *visible* viewport rather than the layout
// viewport — on mobile, opening the soft keyboard shrinks only the visual
// viewport, and without this the sticky header/footer can end up outside the
// area the user can actually see. Falls back to window.innerHeight when
// visualViewport isn't supported (older browsers, jsdom in tests).
function useVisualViewportBox() {
    const getBox = () => {
        if (typeof window === 'undefined') return { height: 0, offsetTop: 0 };
        const vv = window.visualViewport;
        return vv
            ? { height: vv.height, offsetTop: vv.offsetTop || 0 }
            : { height: window.innerHeight, offsetTop: 0 };
    };

    const [box, setBox] = useState(getBox);

    useEffect(() => {
        const vv = window.visualViewport;
        const update = () => setBox(getBox());
        if (vv) {
            vv.addEventListener('resize', update);
            vv.addEventListener('scroll', update);
            return () => {
                vv.removeEventListener('resize', update);
                vv.removeEventListener('scroll', update);
            };
        }
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    return box;
}

// Categorical mode: map a Pass/Fail-style label to an MUI Button colour.
function categoryButtonColor(label) {
    const s = String(label).toLowerCase();
    if (/(pass|accept|good|conform|ok|go\b)/.test(s)) return 'success';
    if (/(fail|reject|defect|bad|nonconform|no.?go|scrap)/.test(s)) return 'error';
    return 'primary';
}

function GuidedEntry({
    contextLabel   = 'Guided Entry',
    slots          = [],
    values         = [],
    onSubmitValue,
    onClose,
    backLabel      = 'Back to Table',
    submitLabel    = 'Record (Enter)',
    categories     = null,   // when a non-empty array → categorical (Pass/Fail) mode
    appendMode     = false,
    specLimits     = null,   // optional {usl,lsl,target} — context strip + run chart
    contextChips   = [],     // optional Array<{label,value}> — extra context strip chips
}) {
    const inputRef = useRef(null);
    const [inputVal, setInputVal]   = useState('');
    const [flash, setFlash]         = useState(false);
    const viewportBox = useVisualViewportBox();
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    // Categorical mode (attribute Pass/Fail entry) when `categories` is supplied;
    // otherwise the original numeric-measurement mode.
    const categorical = Array.isArray(categories) && categories.length > 0;

    // Current slot index: in appendMode this equals values.length (always
    // pointing at the next-to-add position); in slot mode it advances through
    // unfilled (null/undefined) slots in order.
    const currentIndex = appendMode
        ? values.length
        : values.findIndex(v => v === null || v === undefined);

    // When all slots are filled in slot mode, currentIndex is -1.
    const allFilled = !appendMode && currentIndex === -1 && slots.length > 0;

    // The label to display for "what you are measuring right now"
    const currentSlotLabel = appendMode
        ? `Measurement ${currentIndex + 1}`
        : (allFilled ? 'Done' : (slots[currentIndex]?.label ?? `Slot ${currentIndex + 1}`));

    // The last recorded value (for confirmation feedback). Memoized on the values
    // prop so the run chart (React.memo) keeps a stable array identity between the
    // per-keystroke re-renders caused by inputVal state — the chart then only
    // re-renders when a value is actually recorded, not on every wedge character.
    const filledValues = useMemo(() => values.filter(v => v !== null && v !== undefined), [values]);
    const lastValue = filledValues.length > 0 ? filledValues[filledValues.length - 1] : null;

    // Compact "Run chart" of entered values — only where there's room (>= md
    // breakpoint) and only in numeric modes (categorical values aren't a
    // plottable run). Needs >= 2 points to be worth drawing.
    const showRunChart = !categorical && isMdUp && filledValues.length >= 2;

    // Auto-focus on mount and whenever the active index changes
    useEffect(() => {
        if (!allFilled && inputRef.current) inputRef.current.focus();
    }, [currentIndex, allFilled]);

    const submit = useCallback(() => {
        if (allFilled) return;
        const n = parseFloat(inputVal);
        if (!isNaN(n) && isFinite(n)) {
            const idx = appendMode ? values.length : currentIndex;
            onSubmitValue(n, idx);
            setInputVal('');
            setFlash(true);
            setTimeout(() => setFlash(false), 400);
        }
    }, [inputVal, onSubmitValue, currentIndex, appendMode, values.length, allFilled]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            submit();
        }
    };

    const submitCategory = useCallback((cat) => {
        if (allFilled) return;
        const idx = appendMode ? values.length : currentIndex;
        onSubmitValue(cat, idx);
        setFlash(true);
        setTimeout(() => setFlash(false), 400);
    }, [onSubmitValue, currentIndex, appendMode, values.length, allFilled]);

    // Categorical mode: number keys 1..N pick the Nth category (fast keyboard / wedge).
    useEffect(() => {
        if (!categorical || allFilled) return undefined;
        const onKey = (e) => {
            const n = parseInt(e.key, 10);
            if (!Number.isNaN(n) && n >= 1 && n <= categories.length) {
                e.preventDefault();
                submitCategory(categories[n - 1]);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [categorical, allFilled, categories, submitCategory]);

    const recordedCount = appendMode ? values.length : filledValues.length;
    const totalCount    = appendMode ? null : slots.length;
    const itemNoun      = categorical ? 'ratings' : 'measurements';
    const entryHint     = categorical
        ? `tap a category or press 1–${categorical ? categories.length : ''}`
        : 'press Enter or Tab to submit each value';

    return (
        <Box
            role="dialog"
            aria-modal="true"
            aria-label={contextLabel}
            sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                zIndex: 1300,
                backgroundColor: GUIDED_BG,
                display: 'flex',
                flexDirection: 'column',
            }}
            style={{ top: `${viewportBox.offsetTop}px`, height: `${viewportBox.height}px` }}
        >
            {/* ---- Sticky context header (above the fold) ---- */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: GUIDED_BG,
                    zIndex: 1,
                    px: 3,
                    pt: 3,
                    pb: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2 }}>
                    <Typography variant="h6" sx={{ color: '#e3f2fd', fontWeight: 600 }}>
                        {contextLabel}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={onClose}
                        sx={{
                            color: '#90caf9',
                            borderColor: 'rgba(144,202,249,0.5)',
                            '&:hover': { borderColor: '#90caf9', backgroundColor: 'rgba(144,202,249,0.08)' },
                            flexShrink: 0,
                        }}
                    >
                        {backLabel}
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {allFilled ? (
                        <Chip
                            label="All measurements recorded"
                            size="small"
                            sx={{ backgroundColor: 'rgba(129,199,132,0.15)', color: '#81c784', fontWeight: 700, fontSize: '0.85rem' }}
                        />
                    ) : (
                        <Chip
                            label={currentSlotLabel}
                            size="small"
                            sx={{ backgroundColor: 'rgba(144,202,249,0.15)', color: '#90caf9', fontWeight: 700, fontSize: '0.85rem' }}
                        />
                    )}
                    {lastValue !== null && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: flash ? '#81c784' : 'rgba(255,255,255,0.55)',
                                transition: 'color 0.2s',
                                fontSize: '0.85rem',
                            }}
                        >
                            Last: <strong>{lastValue}</strong>
                        </Typography>
                    )}
                </Box>

                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', mt: 1, display: 'block' }}>
                    {totalCount !== null
                        ? `${recordedCount} / ${totalCount} ${itemNoun} recorded${!allFilled ? ' — ' + entryHint : ''}`
                        : `${recordedCount} ${itemNoun.slice(0, -1)}${recordedCount !== 1 ? 's' : ''} entered — ${entryHint}`
                    }
                </Typography>
            </Box>

            {/* ---- Spacer — becomes a live run chart at >= tablet widths ---- */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, overflow: 'hidden' }}>
                {showRunChart && <GuidedEntryRunChart values={filledValues} specLimits={specLimits} />}
            </Box>

            {/* ---- Input anchored near bottom ---- */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: GUIDED_BG,
                    px: 3,
                    py: 4,
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 2,
                }}
            >
                {/* ---- Pinned context strip — sits directly above the input so
                       progress + spec limits stay visible together with the
                       input when a mobile soft keyboard is open. ---- */}
                <Box
                    data-testid="ge-context-strip"
                    sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}
                >
                    <Chip
                        label={totalCount !== null ? `${recordedCount} of ${totalCount}` : `${recordedCount} entered`}
                        size="small"
                        sx={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                    {specLimits && specLimits.lsl !== undefined && specLimits.lsl !== null && specLimits.lsl !== '' && (
                        <Chip
                            label={`LSL ${specLimits.lsl}`}
                            size="small"
                            sx={{ backgroundColor: 'rgba(239,83,80,0.12)', color: '#ef9a9a', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                    )}
                    {specLimits && specLimits.usl !== undefined && specLimits.usl !== null && specLimits.usl !== '' && (
                        <Chip
                            label={`USL ${specLimits.usl}`}
                            size="small"
                            sx={{ backgroundColor: 'rgba(239,83,80,0.12)', color: '#ef9a9a', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                    )}
                    {specLimits && specLimits.target !== undefined && specLimits.target !== null && specLimits.target !== '' && (
                        <Chip
                            label={`Target ${specLimits.target}`}
                            size="small"
                            sx={{ backgroundColor: 'rgba(129,199,132,0.12)', color: '#a5d6a7', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                    )}
                    {contextChips.map((c, i) => (
                        <Chip
                            key={`ctx-chip-${i}`}
                            label={`${c.label}: ${c.value}`}
                            size="small"
                            sx={{ backgroundColor: 'rgba(144,202,249,0.10)', color: '#90caf9', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                    ))}
                </Box>
                {allFilled ? (
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={onClose}
                        sx={{ fontSize: '1rem', py: 1.5 }}
                    >
                        Done — Back to Table
                    </Button>
                ) : categorical ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                        {categories.map((c, i) => (
                            <Button
                                key={c}
                                variant="contained"
                                color={categoryButtonColor(c)}
                                size="large"
                                onClick={() => submitCategory(c)}
                                sx={{ minWidth: 150, py: 2, fontSize: '1.15rem', fontWeight: 700, textTransform: 'none' }}
                            >
                                {i + 1}. {c}
                            </Button>
                        ))}
                    </Box>
                ) : (
                    <>
                        <input
                            ref={inputRef}
                            type="number"
                            inputMode="decimal"
                            aria-label={currentSlotLabel ? `Measurement for ${currentSlotLabel}` : 'Measurement value'}
                            value={inputVal}
                            onChange={(e) => setInputVal(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter measurement…"
                            style={{
                                fontSize: '2rem',
                                padding: '16px 20px',
                                backgroundColor: 'rgba(255,255,255,0.06)',
                                border: '2px solid rgba(144,202,249,0.5)',
                                borderRadius: 8,
                                color: '#e3f2fd',
                                outline: 'none',
                                textAlign: 'center',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={submit}
                            startIcon={<CheckCircleIcon />}
                            sx={{ fontSize: '1rem', py: 1.5 }}
                        >
                            {submitLabel}
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
}

export default GuidedEntry;
